import { DEFAULT_PROMPTBOOK_NAME } from "@/constants";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { createResource } from "@/features/Resources/actions/createResource";
import { useWorkspaces } from "@/features/Workspaces/hooks/useWorkspaces";
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Icon } from "@pufflig/prompt-studio-ui";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createPromptbookFromTemplate } from "../actions/createPromptbookFromTemplate";
import { usePromptbookTemplates } from "../hooks/usePromptbookTemplates";
import { CreatePromptbookForm } from "./CreatePromptbookForm";

interface CreatePromptbookModalProps {
  selectedTemplateId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenTemplateModal?: () => void;
}

export const CreatePromptbookModal = (props: CreatePromptbookModalProps) => {
  const { isOpen, onClose, selectedTemplateId, onOpenTemplateModal } = props;
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("team") || undefined;

  const { data: workspaces } = useWorkspaces(teamId);
  const { data: templateList } = usePromptbookTemplates(teamId);
  const { navigateToPromptbook } = useAppNavigation();

  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState(DEFAULT_PROMPTBOOK_NAME);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(workspaces?.[0]?.workspace_id || null);

  const selectedTemplate = templateList?.find((template) => template.resource_id === selectedTemplateId);
  const templateImageUrl = selectedTemplate?.thumbnail_url || undefined;
  const templateName = selectedTemplate?.name || "Start from scratch";
  const templateDescription = selectedTemplate?.description || "Create a new Promptbook from scratch.";

  useEffect(() => {
    if (workspaces && !selectedWorkspace) {
      setSelectedWorkspace(workspaces?.[0]?.workspace_id);
    }

    if (!workspaces?.length) {
      setSelectedWorkspace(null);
    }
  }, [workspaces]);

  return (
    <Modal variant={"large"} isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader padding="spacing-lg">
          <Text textStyle="heading/2">New Promptbook</Text>
          <ModalCloseButton mt="4px" />
        </ModalHeader>
        <ModalBody>
          <CreatePromptbookForm
            promptbookName={name}
            selectedWorkspace={selectedWorkspace}
            onRenamePromptbook={setName}
            onSelectWorkspace={(workspaceId) => setSelectedWorkspace(workspaceId)}
            templateDescription={templateDescription}
            templateImageUrl={templateImageUrl}
            templateName={templateName}
            workspaces={workspaces || []}
          />
        </ModalBody>
        <ModalFooter>
          <Stack direction="row" gap="1" width="100%" justify={onOpenTemplateModal ? "space-between" : "flex-end"}>
            <Button
              alignSelf="flex-start"
              leftIcon={onOpenTemplateModal && <Icon name="arrow-left" />}
              onClick={() => {
                onClose();
                onOpenTemplateModal && onOpenTemplateModal();
              }}
            >
              {onOpenTemplateModal ? "Back" : "Cancel"}
            </Button>
            <Button
              isDisabled={!selectedWorkspace}
              isLoading={isCreating}
              variant="primary"
              onClick={async () => {
                if (!selectedWorkspace) return;

                setIsCreating(true);

                if (!selectedTemplateId) {
                  const { resourceId } = await createResource(name, selectedWorkspace, "promptbook", teamId);
                  if (resourceId) navigateToPromptbook(resourceId);
                  return;
                }

                const { newPromptbookId } = await createPromptbookFromTemplate(
                  selectedTemplateId,
                  name,
                  selectedWorkspace,
                  teamId,
                );

                if (newPromptbookId) {
                  navigateToPromptbook(newPromptbookId);
                }

                setIsCreating(false);
              }}
            >
              Create Promptbook
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
